import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react';
import { Moment } from 'moment';
import { asyncForEach } from '@shared/utils/array';
import { toMoment } from '@shared/utils/date';
import { PortRotationEntryGeneratorTypes } from '@shared/utils/port-rotation-entry-generator-types';
import getPortUtcOffset from '@shared/utils/getPortUtcOffset';
import type { UpdateEstimateAttributes } from '@api/features/estimates/updateEstimate';
import type { EditableCargo } from '@api/features/cargos/updateCargo';
import type { EstimatesWithResults } from '@api/features/estimates/getEstimateGroup';
import type { Port } from '@api/utils/ports';
import {
	updateCargo,
	updateEstimate,
} from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import { useCentralStore } from './CentralStoreContext';
import { usePortRotationProvider } from './PortRotationContext';
import { saveMiscCost } from './hooks/useMiscEstimateCostHandler';

export type BallastLegChanges = {
	port?: number | null;
	departureDate?: Moment | null;
};

type GridChangesContextType = {
	pendingEstimateChanges: Record<number, UpdateEstimateAttributes>;
	pendingCargoChanges: Record<number, Partial<EditableCargo>>;
	pendingBallastLegChanges: Record<number, BallastLegChanges>;
	onUpdateGridEstimate: (estimateId: number, attributes: UpdateEstimateAttributes) => void;
	onUpdateGridCargo: (cargoId: number, attributes: Partial<EditableCargo>) => void;
	onUpdateGridBallastLeg: (estimateId: number, changes: BallastLegChanges) => void;
	hasPendingGridChanges: boolean;
	savingGrid: boolean;
	onSaveGridChanges: () => Promise<void>;
	onClearGridChanges: () => void;
};

const GridChangesContext = createContext<GridChangesContextType>(undefined!);
export const useGridChanges = () => useContext(GridChangesContext);

const getPortRotationWithBallastLeg = (
	estimate: EstimatesWithResults[number],
	changes: BallastLegChanges,
	ports: Port[],
) => {
	const portRotation = [...estimate.EstimatePortRotationEntries];
	const ballastLegIndex = portRotation.findIndex((pr) => (
		pr.generatedBy === PortRotationEntryGeneratorTypes.BALLAST_LEG
	));

	if (ballastLegIndex === -1) {
		return null;
	}

	const ballastLeg = portRotation[ballastLegIndex];
	const port = changes.port !== undefined ?
		ports.find((p) => p.id === changes.port) ?? null :
		ballastLeg.port;
	let departureDate = changes.departureDate !== undefined ?
		changes.departureDate :
		ballastLeg.departureDate;

	if (departureDate != null) {
		const base = toMoment(departureDate);

		if (base.isValid()) {
			const offset = port != null ?
				getPortUtcOffset(port.id, base) :
				base.utcOffset();
			departureDate = base.clone().utcOffset(offset);
		}
	}

	portRotation[ballastLegIndex] = {
		...ballastLeg,
		port,
		departureDate,
	};

	return portRotation;
};

const GridChangesProvider = ({ children }: { children: React.ReactNode }) => {
	const {
		estimates,
		refreshEverything,
	} = useCentralStore();

	const { ports } = usePortRotationProvider();

	const [savingGrid, setSavingGrid] = useState(false);
	const [
		pendingEstimateChanges,
		setPendingEstimateChanges,
	] = useState<Record<number, UpdateEstimateAttributes>>({});
	const [
		pendingCargoChanges,
		setPendingCargoChanges,
	] = useState<Record<number, Partial<EditableCargo>>>({});
	const [
		pendingBallastLegChanges,
		setPendingBallastLegChanges,
	] = useState<Record<number, BallastLegChanges>>({});

	const onUpdateGridEstimate = useCallback((
		estimateId: number,
		attributes: UpdateEstimateAttributes,
	) => {
		setPendingEstimateChanges((prev) => ({
			...prev,
			[estimateId]: { ...prev[estimateId], ...attributes },
		}));
	}, []);

	const onUpdateGridCargo = useCallback((
		cargoId: number,
		attributes: Partial<EditableCargo>,
	) => {
		setPendingCargoChanges((prev) => ({
			...prev,
			[cargoId]: { ...prev[cargoId], ...attributes },
		}));
	}, []);

	const onUpdateGridBallastLeg = useCallback((
		estimateId: number,
		changes: BallastLegChanges,
	) => {
		setPendingBallastLegChanges((prev) => ({
			...prev,
			[estimateId]: { ...prev[estimateId], ...changes },
		}));
	}, []);

	const hasPendingGridChanges = useMemo(() => (
		Object.keys(pendingEstimateChanges).length > 0 ||
		Object.keys(pendingCargoChanges).length > 0 ||
		Object.keys(pendingBallastLegChanges).length > 0
	), [pendingEstimateChanges, pendingCargoChanges, pendingBallastLegChanges]);

	const onClearGridChanges = useCallback(() => {
		setPendingEstimateChanges({});
		setPendingCargoChanges({});
		setPendingBallastLegChanges({});
	}, []);

	const onSaveGridChanges = useCallback(async () => {
		setSavingGrid(true);

		try {
			const estimateIds = [...new Set([
				...Object.keys(pendingEstimateChanges),
				...Object.keys(pendingBallastLegChanges),
			].map(Number))];

			await asyncForEach(estimateIds, async (estimateId) => {
				const estimate = estimates?.find((e) => e.id === estimateId);

				if (estimate == null) {
					return;
				}

				const attributes = { ...pendingEstimateChanges[estimateId] };
				const ballastLegChanges = pendingBallastLegChanges[estimateId];

				if (ballastLegChanges != null) {
					const portRotationEntries = getPortRotationWithBallastLeg(estimate, ballastLegChanges, ports ?? []);

					if (portRotationEntries != null) {
						attributes.portRotationEntries = portRotationEntries;
					}
				}

				if (attributes.miscCostTotal != null) {
					await saveMiscCost({
						estimateId,
						currency: estimate.currency,
						estimateCosts: estimate.EstimateCosts,
						value: attributes.miscCostTotal,
					});
				}

				if (Object.keys(attributes).length > 0) {
					await updateEstimate(estimateId, attributes, false, false);
				}
			});

			await asyncForEach(Object.entries(pendingCargoChanges), async ([cargoId, attributes]) => {
				await updateCargo({ cargoId: Number(cargoId), attributes }, false);
			});

			onClearGridChanges();
		} catch (e) {
			showErrorNotification('Could not save changes', e as Error);
		} finally {
			await refreshEverything();
			setSavingGrid(false);
		}
	}, [
		estimates,
		ports,
		pendingEstimateChanges,
		pendingCargoChanges,
		pendingBallastLegChanges,
		onClearGridChanges,
		refreshEverything,
	]);

	return (
		<GridChangesContext.Provider
			value={{
				pendingEstimateChanges,
				pendingCargoChanges,
				pendingBallastLegChanges,
				onUpdateGridEstimate,
				onUpdateGridCargo,
				onUpdateGridBallastLeg,
				hasPendingGridChanges,
				savingGrid,
				onSaveGridChanges,
				onClearGridChanges,
			}}
		>
			{children}
		</GridChangesContext.Provider>
	);
};

export default GridChangesProvider;
